import { Controller } from "@hotwired/stimulus"

// Displays a product hovercard on mouse hover.
// Content is pre-rendered (no async fetch) since data is already loaded.
// Card is moved to <body> on connect to escape table stacking contexts. When the
// trigger sits inside a modal <dialog> (top layer, so no z-index on <body> can
// beat it), the card is re-parented into that dialog when shown.
export default class extends Controller {
  static targets = ["card", "trigger"]

  connect() {
    this.showTimeout = null
    this.hideTimeout = null
    this.mouseX = 0
    this.mouseY = 0

    this.card = this.cardTarget

    // Bind card hover listeners directly — card is moved outside the controller
    // element so Stimulus data-action delegation doesn't reach it.
    this._onCardEnter = this.cardMouseEnter.bind(this)
    this._onCardLeave = this.cardMouseLeave.bind(this)
    this.card.addEventListener("mouseenter", this._onCardEnter)
    this.card.addEventListener("mouseleave", this._onCardLeave)

    document.body.appendChild(this.card)
  }

  disconnect() {
    this.clearTimeouts()
    this.card.classList.add("hidden")
    this.card.removeEventListener("mouseenter", this._onCardEnter)
    this.card.removeEventListener("mouseleave", this._onCardLeave)
    this.element.appendChild(this.card)
  }

  mouseEnter(event) {
    this.mouseX = event.clientX
    this.mouseY = event.clientY
    this.clearTimeouts()
    this.showTimeout = setTimeout(() => {
      this.showCard()
    }, 300)
  }

  mouseLeave() {
    this.clearTimeouts()
    this.hideTimeout = setTimeout(() => {
      this.hideCard()
    }, 350)
  }

  mouseMove(event) {
    this.mouseX = event.clientX
    this.mouseY = event.clientY
  }

  cardMouseEnter() {
    this.clearTimeouts()
  }

  cardMouseLeave() {
    this.hideCard()
  }

  showCard() {
    const host = this.element.closest("dialog[open]") || document.body
    if (this.card.parentNode !== host) host.appendChild(this.card)
    this.card.classList.remove("hidden")
    this.positionCard()
  }

  hideCard() {
    this.card.classList.add("hidden")
  }

  clearTimeouts() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
      this.showTimeout = null
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
  }

  positionCard() {
    const card = this.card
    const cardWidth = card.offsetWidth
    const cardHeight = card.offsetHeight
    const pad = 12

    // Position to the right of the cursor by default
    let left = this.mouseX + pad
    // If it would overflow the right edge, flip to the left of the cursor
    if (left + cardWidth > window.innerWidth - pad) {
      left = this.mouseX - cardWidth - pad
    }
    // Clamp to viewport
    if (left < pad) left = pad

    // Position below the cursor, offset slightly
    let top = this.mouseY - 20
    // Keep within viewport
    if (top + cardHeight > window.innerHeight - pad) {
      top = window.innerHeight - cardHeight - pad
    }
    if (top < pad) top = pad

    card.style.top = `${top}px`
    card.style.left = `${left}px`
  }
}
